import { Box, Flex, Link, Tooltip } from '@chakra-ui/react';
import { ArrowDownTrayIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Avatar } from '@main/complykit/components/avatar';
import { Text } from '@main/complykit/components/text';
import { formatDate } from '@main/shared/utils';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useDownloadStorageFile } from '../hooks';
import { OverflowContainer } from '../overflow-tooltip';
import { Card, CardActionsProps, CardTagsProps } from './base-card';
import type { FileCardPreview } from './upload-file-card';

interface VersionCardBaseProps extends CardActionsProps, CardTagsProps {
  size?: 'sm' | 'md';
  validityStart?: string;
  createdBy?: string;
  icon?: ReactNode;
  fileNamePrefix?: ReactNode;
  preview?: FileCardPreview;
  onDownload?: () => void;
  isDownloading?: boolean;
  showDownloadAction?: boolean;
}

type VersionCardConditionalProps =
  | {
      url: string | undefined | null;
      urlLabel?: ReactNode | null;
      fileId?: string | null;
      fileName?: string | null;
    }
  | {
      fileId: string | undefined | null;
      fileName: string | undefined | null;
      url?: string | null;
      urlLabel?: ReactNode | null;
    };

type VersionCardProps = VersionCardBaseProps & VersionCardConditionalProps;

export const CurrentVersionCard = ({
  size,
  url,
  urlLabel,
  fileId,
  fileName,
  validityStart,
  createdBy,
  actions,
  tags,
  numberOfInlineActions,
  icon,
  fileNamePrefix,
  preview,
  onDownload,
  isDownloading,
  showDownloadAction = true,
}: VersionCardProps) => {
  const { t } = useTranslation('ui');
  const downloadFile = useDownloadStorageFile();
  const copyToClipboard = (url: string) => navigator.clipboard.writeText(url);
  const hasFileNamePrefix = !url && Boolean(fileNamePrefix);

  return (
    <Card
      size={size}
      onPreview={fileId && !url && !preview?.isPreviewDisabled ? preview?.onPreview : undefined}
      previewLabel={fileName ?? undefined}
      title={preview?.isPreviewDisabled ? preview.previewDisabledReason : undefined}
    >
      <Card.Header
        left={
          <Flex gap={4}>
            {icon}
            {tags?.length ? <Card.Tags tags={tags} /> : null}
          </Flex>
        }
        right={
          <Flex gap={4} alignItems="center">
            <Tooltip
              label={t('cards.validityDate')}
              placement="top"
              hasArrow
              gutter={12}
              openDelay={600}
            >
              <Text size="xs" color="muted" isNoWrap>
                {formatDate(validityStart)}
              </Text>
            </Tooltip>
            {createdBy && (
              <Tooltip
                label={createdBy}
                placement="top"
                hasArrow
                gutter={12}
                openDelay={600}
                shouldWrapChildren
              >
                <Avatar name={createdBy} size="sm" />
              </Tooltip>
            )}
          </Flex>
        }
        actions={[
          fileId && showDownloadAction
            ? {
                icon: ArrowDownTrayIcon,
                label: t('cards.actions.download'),
                onClick: onDownload ?? (() => downloadFile(fileId)),
                isLoading: isDownloading,
                isRead: true,
              }
            : undefined,
          url
            ? {
                icon: DocumentDuplicateIcon,
                label: t('cards.actions.copyLink'),
                onClick: () => copyToClipboard(url),
                isRead: true,
              }
            : undefined,
          ...(actions ? [...actions] : []),
        ]}
        numberOfInlineActions={numberOfInlineActions ?? 3}
      />

      <Card.Body gap={hasFileNamePrefix ? 3 : 2} ml={hasFileNamePrefix ? -1 : undefined}>
        {!url && fileNamePrefix}
        <OverflowContainer>
          <Box maxW={['3xs', '2xs']}>
            <OverflowContainer.Tooltip
              label={url ?? fileName}
              placement="top"
              gutter={12}
              openDelay={500}
            >
              {url ? (
                <Link
                  noOfLines={1}
                  fontSize="sm"
                  isExternal
                  href={url ?? undefined}
                  lineHeight={7}
                  color={urlLabel ? 'blackAlpha.800' : 'blue.400'}
                  /* This is important to make sure that very long link will not push action buttons out of the card */
                  maxW={['3xs', '2xs']}
                  _dark={{ color: urlLabel ? 'whiteAlpha.800' : 'blue.200' }}
                >
                  {urlLabel ?? url}
                </Link>
              ) : (
                <Text size="sm" weight="medium" isTruncated>
                  {fileName}
                </Text>
              )}
            </OverflowContainer.Tooltip>
          </Box>
        </OverflowContainer>
      </Card.Body>
    </Card>
  );
};
